/**
 * Azure AD Authentication Service
 *
 * Handles Azure AD / Microsoft authentication using MSAL (Microsoft Authentication Library).
 * This enables users to sign in with their Microsoft/Azure AD organizational accounts.
 *
 * Flow:
 * 1. User clicks "Sign in with Microsoft"
 * 2. MSAL redirects to Azure AD login page
 * 3. User authenticates with their Microsoft account
 * 4. Azure AD redirects back to the callback page
 * 5. MSAL handles the response and returns tokens
 * 6. App upserts user profile and stores session
 */

import {
  AccountInfo,
  AuthenticationResult,
  BrowserAuthError,
  InteractionRequiredAuthError,
  PopupRequest,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import {
  getMsalInstance,
  initializeMsal,
  isAzureConfigured,
  loginScopes,
  userSyncScopes,
} from '../lib/msalConfig';

// =============================================================================
// TYPES
// =============================================================================

export interface AzureUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

export interface AzureAuthResult {
  user: AzureUser;
  accessToken: string;
  idToken?: string;
  expiresOn?: Date;
}

// =============================================================================
// SERVICE
// =============================================================================

export const azureAuthService = {
  /**
   * Check if Azure AD authentication is configured
   */
  isConfigured(): boolean {
    return isAzureConfigured();
  },

  /**
   * Initialize MSAL and handle any pending redirects
   * Should be called on app startup
   */
  async initialize(): Promise<AuthenticationResult | null> {
    const msalInstance = await initializeMsal();
    if (!msalInstance) {
      return null;
    }

    try {
      // Handle redirect response if coming back from Azure AD
      const response = await msalInstance.handleRedirectPromise();
      return response;
    } catch (error) {
      console.error('[AzureAuth] Error handling redirect:', error);
      return null;
    }
  },

  /**
   * Get the currently signed-in account
   */
  getAccount(): AccountInfo | null {
    const msalInstance = getMsalInstance();
    if (!msalInstance) {
      return null;
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return null;
    }

    // Return the first account (or implement account selection if needed)
    return accounts[0];
  },

  /**
   * Check if an MSAL interaction is currently in progress
   */
  isInteractionInProgress(): boolean {
    const msalInstance = getMsalInstance();
    if (!msalInstance) {
      return false;
    }
    // Check if there's an active interaction by looking for interaction status in browser storage
    const interactionStatus = sessionStorage.getItem('msal.interaction.status');
    return interactionStatus !== null && interactionStatus !== '';
  },

  /**
   * Clear any stuck interaction state
   * Use when interaction_in_progress error occurs unexpectedly
   */
  clearInteractionState(): void {
    // Clear MSAL interaction status from session storage
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('msal.') && key.includes('interaction')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  },

  /**
   * Sign in with Microsoft using redirect flow
   * Recommended for most scenarios
   */
  async signInWithRedirect(): Promise<void> {
    const msalInstance = getMsalInstance();
    if (!msalInstance) {
      throw new Error('Azure AD not configured. Please contact your administrator.');
    }

    const request: RedirectRequest = {
      ...loginScopes,
      prompt: 'select_account', // Always show account picker
    };

    try {
      // Wait for any pending interactions to complete
      await msalInstance.handleRedirectPromise();
      await msalInstance.loginRedirect(request);
    } catch (error) {
      // Handle interaction_in_progress error
      if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
        console.warn('[AzureAuth] Interaction already in progress, clearing state and retrying...');
        // Clear stuck interaction state and retry once
        this.clearInteractionState();
        try {
          await msalInstance.loginRedirect(request);
        } catch (retryError) {
          console.error('[AzureAuth] Retry failed after clearing interaction state:', retryError);
          throw new Error('Login is already in progress. Please wait for the current login to complete, or refresh the page and try again.');
        }
        return;
      }
      console.error('[AzureAuth] Redirect login error:', error);
      throw error;
    }
  },

  /**
   * Sign in with Microsoft using popup flow
   * Alternative flow that doesn't navigate away from the page
   */
  async signInWithPopup(): Promise<AzureAuthResult> {
    const msalInstance = getMsalInstance();
    if (!msalInstance) {
      throw new Error('Azure AD not configured. Please contact your administrator.');
    }

    const request: PopupRequest = {
      ...loginScopes,
      prompt: 'select_account',
    };

    try {
      // Wait for any pending interactions to complete
      await msalInstance.handleRedirectPromise();
      const response = await msalInstance.loginPopup(request);
      return this.parseAuthResult(response);
    } catch (error) {
      // Handle interaction_in_progress error
      if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
        console.warn('[AzureAuth] Interaction already in progress, clearing state and retrying...');
        // Clear stuck interaction state and retry once
        this.clearInteractionState();
        try {
          const response = await msalInstance.loginPopup(request);
          return this.parseAuthResult(response);
        } catch (retryError) {
          console.error('[AzureAuth] Retry failed after clearing interaction state:', retryError);
          throw new Error('Login is already in progress. Please wait for the current login to complete, or refresh the page and try again.');
        }
      }
      console.error('[AzureAuth] Popup login error:', error);
      throw error;
    }
  },

  /**
   * Sign out from Microsoft
   */
  async signOut(): Promise<void> {
    const msalInstance = getMsalInstance();
    if (!msalInstance) {
      return;
    }

    const account = this.getAccount();
    if (account) {
      try {
        await msalInstance.logoutRedirect({
          account,
          postLogoutRedirectUri: window.location.origin,
        });
      } catch (error) {
        console.error('[AzureAuth] Logout error:', error);
        // Clear local state even if logout fails
        msalInstance.clearCache();
      }
    }
  },

  /**
   * Get access token silently (for API calls to Microsoft Graph)
   * Falls back to popup if silent acquisition fails
   */
  async getAccessToken(scopes?: string[]): Promise<string | null> {
    const result = await this.getTokens(scopes);
    return result?.accessToken || null;
  },

  /**
   * Get both access token and ID token silently
   * - Access token: for Microsoft Graph API calls (audience: MS Graph)
   * - ID token: for our own API authentication (audience: our Azure client ID)
   * Falls back to popup if silent acquisition fails
   */
  async getTokens(scopes?: string[]): Promise<{ accessToken: string; idToken: string } | null> {
    const msalInstance = getMsalInstance();
    if (!msalInstance) {
      console.error('[AzureAuth] MSAL not configured');
      return null;
    }

    const account = this.getAccount();
    if (!account) {
      console.error('[AzureAuth] No account found. User may need to sign in first.');
      return null;
    }

    const request: SilentRequest = {
      scopes: scopes || loginScopes.scopes,
      account,
    };

    try {
      const response = await msalInstance.acquireTokenSilent(request);
      return {
        accessToken: response.accessToken,
        idToken: response.idToken,
      };
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired or needs re-authentication - try popup
        console.info('[AzureAuth] Silent acquisition failed, trying popup for scopes:', scopes);
        try {
          const response = await msalInstance.acquireTokenPopup({
            ...request,
            prompt: 'consent', // Explicitly request consent for new scopes
          });
          return {
            accessToken: response.accessToken,
            idToken: response.idToken,
          };
        } catch (popupError: unknown) {
          const errorMessage = popupError instanceof Error ? popupError.message : String(popupError);
          console.error('[AzureAuth] Popup token acquisition error:', errorMessage);
          // Check for common popup issues
          if (errorMessage.includes('popup_window_error') || errorMessage.includes('block')) {
            console.error('[AzureAuth] Popup may have been blocked by browser. Try allowing popups for this site.');
          }
          if (errorMessage.includes('user_cancelled')) {
            console.error('[AzureAuth] User cancelled the consent prompt.');
          }
          return null;
        }
      }
      // Log detailed error for debugging
      const errorDetails = error instanceof Error ? error.message : String(error);
      console.error('[AzureAuth] Silent token acquisition error:', errorDetails);
      return null;
    }
  },

  /**
   * Get access token for user sync (requires admin consent for User.Read.All)
   */
  async getUserSyncToken(): Promise<string | null> {
    return this.getAccessToken(userSyncScopes.scopes);
  },

  /**
   * Parse authentication result into a user-friendly format
   */
  parseAuthResult(response: AuthenticationResult): AzureAuthResult {
    const account = response.account;

    // Extract user info from the account and claims
    const user: AzureUser = {
      id: account?.localAccountId || account?.homeAccountId || '',
      name: account?.name || '',
      email: account?.username || '',
      avatarUrl: undefined, // Azure doesn't provide avatar in token, requires Graph API call
    };

    return {
      user,
      accessToken: response.accessToken,
      idToken: response.idToken,
      expiresOn: response.expiresOn || undefined,
    };
  },

  /**
   * Fetch user photo from Microsoft Graph API
   * Returns a data URL for the avatar
   */
  async fetchUserPhoto(): Promise<string | null> {
    try {
      const token = await this.getAccessToken(['User.Read']);
      if (!token) {
        return null;
      }

      const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // 404 means no photo, which is not an error
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch photo: ${response.status}`);
      }

      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('[AzureAuth] Error fetching user photo:', error);
      return null;
    }
  },

  /**
   * Fetch current user profile from Microsoft Graph API
   */
  async fetchUserProfile(): Promise<AzureUser | null> {
    try {
      const token = await this.getAccessToken(['User.Read']);
      if (!token) {
        return null;
      }

      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch profile: ${response.status}`);
      }

      const profile = await response.json();

      return {
        id: profile.id,
        name: profile.displayName || profile.givenName || '',
        email: profile.mail || profile.userPrincipalName || '',
        avatarUrl: undefined,
      };
    } catch (error) {
      console.error('[AzureAuth] Error fetching user profile:', error);
      return null;
    }
  },

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    return this.getAccount() !== null;
  },

  /**
   * Get stored session info
   */
  getSessionInfo(): { email: string | null; expiresAt: number | null } {
    const account = this.getAccount();
    if (!account) {
      return { email: null, expiresAt: null };
    }

    return {
      email: account.username || null,
      expiresAt: null, // MSAL manages token expiration internally
    };
  },
};

export default azureAuthService;
